"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useTheme } from "next-themes";
import { Moon, Sun, Menu, X, MessageCircle, Sparkles, Phone, UserCircle, LogOut } from "lucide-react";
import Image from "next/image";
import {
  SITE_PHONE_E164,
  SITE_PHONE_DISPLAY,
  SITE_WHATSAPP_URL_DEFAULT,
  SITE_TOOL_ENTRIES,
} from "@/lib/site-config";
import { cn } from "@/lib/utils";
import { getStudentUser, type StudentUser } from "@/lib/student-auth";

const NAV_LINKS = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
  { name: "Courses", path: "/courses" },
  { name: "Batch", path: "/upcoming-batch" },
  { name: "FAQ", path: "/faq" },
  { name: "Tools", path: "/tools" },
  { name: "Reviews", path: "/reviews" },
  { name: "Careers", path: "/careers" },
  { name: "Contact", path: "/contact" },
];

function linkActive(pathname: string | null, href: string) {
  if (!pathname) return false;
  if (href === "/") return pathname === "/";
  return pathname === href || pathname.startsWith(`${href}/`);
}

export function Navbar() {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  const [student, setStudent] = useState<StudentUser | null>(null);

  useEffect(() => {
    setStudent(getStudentUser());
  }, [pathname]);

  const handleLogout = () => {
    setStudent(null);
    window.location.href = "/student/logout";
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: "spring", stiffness: 380, damping: 32 }}
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-[padding] duration-500 ease-out",
        scrolled ? "pt-3 md:pt-4" : "pt-0",
      )}
    >
      {/* Ambient line — reads as premium chrome */}
      <div
        className={cn(
          "pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-mont-violet/50 to-transparent transition-opacity duration-500",
          scrolled ? "opacity-100" : "opacity-40",
        )}
      />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-10">
        <div
          className={cn(
            "relative flex flex-wrap items-center justify-between gap-y-3 transition-all duration-500 ease-out",
            scrolled
              ? "rounded-2xl border border-border/80 bg-background/70 px-3 py-2 shadow-[0_16px_50px_-12px_rgba(0,0,0,0.18)] backdrop-blur-2xl dark:border-white/10 dark:bg-zinc-950/75 dark:shadow-[0_20px_60px_-12px_rgba(0,0,0,0.75)] dark:ring-1 dark:ring-inset dark:ring-white/[0.06]"
              : "border-b border-transparent bg-gradient-to-b from-background/90 via-background/40 to-transparent py-3.5 backdrop-blur-sm dark:from-background/80 dark:via-background/30",
          )}
        >
          {/* Soft glow behind dock when scrolled */}
          {scrolled ? (
            <div
              className="pointer-events-none absolute -inset-px rounded-2xl opacity-60 blur-xl bg-gradient-to-r from-mont-violet/25 via-mont-orange/15 to-mont-blue/20 dark:opacity-40"
              aria-hidden
            />
          ) : null}

          <div className="relative flex min-h-[3rem] flex-1 items-center justify-between gap-3">
            <Link
              href="/"
              className="group relative flex shrink-0 items-center gap-3 rounded-xl outline-none ring-offset-2 ring-offset-background transition-transform duration-300 hover:scale-[1.02] focus-visible:ring-2 focus-visible:ring-mont-violet/60"
            >
              <span className="sr-only">Mont2 Academy — home</span>
              <span className="relative flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden sm:h-14 sm:w-14 md:h-16 md:w-16 lg:h-20 lg:w-20">
                <Image
                  src="/logo_dark.png"
                  alt=""
                  width={140}
                  height={140}
                  className="object-contain p-0.5 dark:hidden"
                  aria-hidden
                  priority
                />
                <Image
                  src="/logo.png"
                  alt=""
                  width={140}
                  height={140}
                  className="hidden object-contain p-0.5 dark:block"
                  aria-hidden
                  priority
                />
              </span>
              <span className="hidden flex-col leading-none sm:flex">
              </span>
            </Link>

            {/* Desktop — pill strip */}
            <nav
              aria-label="Main"
              className="hidden min-w-0 flex-1 items-center justify-center lg:flex lg:px-2 xl:px-2"
            >
              <div className="flex max-w-full flex-wrap items-center justify-center gap-0.5 rounded-full border border-border/50 bg-muted/30 px-1 py-1 shadow-inner dark:border-white/10 dark:bg-white/[0.04] xl:gap-1 xl:px-1.5">
                {NAV_LINKS.map((link) => {
                  const active = linkActive(pathname, link.path);
                  return (
                    <Link
                      key={link.path}
                      href={link.path}
                      className={cn(
                        "relative rounded-full px-2 py-1.5 text-[10px] font-bold uppercase tracking-wide transition-all duration-200 xl:px-3 xl:text-[11px]",
                        active
                          ? "text-foreground shadow-[0_0_24px_rgba(139,92,246,0.25)]"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      {active ? (
                        <motion.span
                          layoutId="nav-pill"
                          className="absolute inset-0 rounded-full border border-mont-violet/35 bg-gradient-to-br from-mont-violet/25 via-card to-mont-orange/10 dark:from-mont-violet/30 dark:via-zinc-900/80 dark:to-mont-orange/15"
                          transition={{ type: "spring", stiffness: 400, damping: 30 }}
                        />
                      ) : null}
                      <span className="relative z-10">{link.name}</span>
                    </Link>
                  );
                })}
              </div>
            </nav>

            {/* Actions — desktop lg+ */}
            <div className="hidden shrink-0 items-center gap-1.5 lg:flex xl:gap-2">
              <button
                type="button"
                onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-border/80 bg-card/60 text-muted-foreground transition-all hover:border-mont-violet/40 hover:text-foreground hover:shadow-[0_0_20px_rgba(139,92,246,0.2)] dark:border-white/10 dark:bg-white/[0.05]"
                aria-label="Toggle theme"
              >
                <Sun className="hidden h-[18px] w-[18px] dark:block" />
                <Moon className="block h-[18px] w-[18px] dark:hidden" />
              </button>
              <Link href={SITE_WHATSAPP_URL_DEFAULT} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm" className="h-10 w-10 rounded-full border-[#25D366]/40 bg-[#25D366]/10 p-0 text-xs font-semibold text-foreground shadow-none transition-all hover:border-[#25D366]/70 hover:bg-[#25D366]/18 hover:shadow-[0_0_24px_rgba(34,197,94,0.25)] dark:text-white">
                  <MessageCircle className="h-5 w-5 text-[#22c55e]" />
                </Button>
              </Link>
              <Link href={`tel:${SITE_PHONE_E164}`} title={SITE_PHONE_DISPLAY}>
                <Button variant="outline" size="sm" className="h-10 w-10 rounded-full border-mont-violet/40 bg-mont-violet/10 p-0 text-xs font-semibold text-foreground shadow-none transition-all hover:border-mont-violet/70 hover:bg-mont-violet/18 hover:shadow-[0_0_24px_rgba(139,92,246,0.25)] dark:text-white">
                  <Phone className="h-5 w-5 text-mont-violet" />
                </Button>
              </Link>

              {/* Student auth button */}
              {student ? (
                <div className="flex items-center gap-1.5">
                  <Link href="/student/dashboard-redirect">
                    <Button size="sm" className="h-10 gap-2 rounded-full bg-gradient-to-r from-mont-violet to-purple-500 px-4 text-xs font-bold text-white shadow-[0_0_20px_rgba(139,92,246,0.35)] hover:opacity-90">
                      <UserCircle className="h-4 w-4" />
                      {student.name.split(" ")[0]}
                    </Button>
                  </Link>
                  <button onClick={handleLogout} className="flex h-10 w-10 items-center justify-center rounded-full border border-border/80 bg-card/60 text-muted-foreground hover:text-red-400 dark:border-white/10" title="Sign out">
                    <LogOut className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <Link href="/student/login">
                  <Button size="sm" className="h-10 rounded-full bg-gradient-to-r from-mont-violet to-purple-500 px-5 text-xs font-bold text-white shadow-[0_0_20px_rgba(139,92,246,0.35)] hover:opacity-90 hover:shadow-[0_0_30px_rgba(139,92,246,0.5)]">
                    Login
                  </Button>
                </Link>
              )}
            </div>

            {/* Mobile & small tablet */}
            <div className="flex items-center gap-1.5 sm:gap-2 lg:hidden">
              <button
                type="button"
                onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-border/80 bg-card/60 text-muted-foreground transition-colors hover:text-foreground dark:border-white/10"
                aria-label="Toggle theme"
              >
                <Sun className="hidden h-[18px] w-[18px] dark:block" />
                <Moon className="block h-[18px] w-[18px] dark:hidden" />
              </button>
              <Link href={SITE_WHATSAPP_URL_DEFAULT} target="_blank" rel="noopener noreferrer">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-10 w-10 rounded-full border-[#25D366]/35 bg-[#25D366]/10 p-0 hover:bg-[#25D366]/20"
                >
                  <MessageCircle className="h-4 w-4 text-[#22c55e]" />
                </Button>
              </Link>
              <Link href={`tel:${SITE_PHONE_E164}`} title={SITE_PHONE_DISPLAY}>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-10 w-10 rounded-full border-mont-violet/35 bg-mont-violet/10 p-0 hover:bg-mont-violet/20"
                >
                  <Phone className="h-4 w-4 text-mont-violet" />
                </Button>
              </Link>
              <button
                type="button"
                onClick={() => setMobileMenuOpen((o) => !o)}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-border/80 bg-card/80 dark:border-white/10"
                aria-expanded={mobileMenuOpen}
                aria-label="Menu"
              >
                {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {mobileMenuOpen ? (
          <>
            <motion.button
              type="button"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
              aria-label="Close menu"
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: -16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ type: "spring", stiffness: 420, damping: 32 }}
              className="fixed left-3 right-3 top-[4.5rem] z-50 max-h-[min(78vh,calc(100dvh-5.5rem))] overflow-hidden rounded-2xl border border-border/80 bg-background/95 shadow-[0_24px_80px_-12px_rgba(0,0,0,0.35)] backdrop-blur-2xl dark:border-white/10 dark:bg-zinc-950/95 dark:shadow-[0_28px_90px_-12px_rgba(0,0,0,0.85)] sm:left-4 sm:right-4 sm:top-24 lg:hidden"
            >
              <div className="border-b border-border/60 bg-gradient-to-r from-mont-violet/10 via-transparent to-mont-orange/10 px-5 py-4">
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-mont-violet">Navigate</p>              </div>
              <div className="max-h-[calc(min(78vh,calc(100dvh-5.5rem))-5.5rem)] overflow-y-auto px-3 py-3">
                {NAV_LINKS.map((link, i) => {
                  const active = linkActive(pathname, link.path);
                  return (
                    <motion.div
                      key={link.path}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.04 + i * 0.035, duration: 0.25 }}
                    >
                      <Link
                        href={link.path}
                        className={cn(
                          "flex items-center justify-between rounded-xl px-4 py-3.5 text-base font-semibold transition-colors",
                          active
                            ? "bg-gradient-to-r from-mont-violet/20 to-mont-orange/10 text-foreground"
                            : "text-foreground/90 hover:bg-muted/60",
                        )}
                      >
                        {link.name}
                        {active ? (
                          <span className="h-2 w-2 rounded-full bg-mont-orange shadow-[0_0_10px_#f97316]" />
                        ) : null}
                      </Link>
                    </motion.div>
                  );
                })}
                <div className="mt-4 space-y-3 border-t border-border/60 px-2 pb-4 pt-5">
                  {student ? (
                    <>
                      <Link href="/student/dashboard-redirect" onClick={() => setMobileMenuOpen(false)}>
                        <Button className="h-12 w-full justify-center gap-2 rounded-xl bg-gradient-to-r from-mont-violet to-purple-500 font-bold text-white">
                          <UserCircle className="h-4 w-4" /> My Dashboard
                        </Button>
                      </Link>
                      <Button variant="outline" onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="h-12 w-full justify-center gap-2 rounded-xl border-red-500/30 text-red-400">
                        <LogOut className="h-4 w-4" /> Sign Out
                      </Button>
                    </>
                  ) : (
                    <Link href="/student/login" onClick={() => setMobileMenuOpen(false)}>
                      <Button className="h-12 w-full justify-center rounded-xl bg-gradient-to-r from-mont-violet to-purple-500 font-bold text-white shadow-[0_0_20px_rgba(139,92,246,0.3)]">
                        Login
                      </Button>
                    </Link>
                  )}
                  <Link href={SITE_WHATSAPP_URL_DEFAULT} target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)}>
                    <Button
                      variant="outline"
                      className="h-12 w-full justify-center gap-2 rounded-xl border-[#25D366]/40 bg-[#25D366]/10 font-semibold hover:bg-[#25D366]/18"
                    >
                      <MessageCircle className="h-4 w-4 text-[#22c55e]" />
                      WhatsApp desk
                    </Button>
                  </Link>
                  <Link href={`tel:${SITE_PHONE_E164}`} onClick={() => setMobileMenuOpen(false)} title={SITE_PHONE_DISPLAY}>
                    <Button
                      variant="outline"
                      className="h-12 w-full justify-center gap-2 rounded-xl border-mont-violet/40 bg-mont-violet/10 font-semibold hover:bg-mont-violet/18"
                    >
                      <Phone className="h-4 w-4 text-mont-violet" />
                      Call Us
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          </>
        ) : null}
      </AnimatePresence>
    </motion.header>
  );
}
